import React, { Fragment, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "../../../button";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";

import { parseCTAs } from "../parse-ctas";
import { handleCTAClick } from "..";

import styles from "../styles.modules.scss";
import { findRelatedMediaByTitle } from "../../../../utils/related-media";
import { useSnowplowContext } from "../../../../analytics/snowplow";
import { Loading } from "../../../loading";
import { useLocalStorageCleaner } from "../campaign-parts/local-storage-cleaner";
import { useImageLoader } from "./hooks/useImageLoader";
import { Character } from "./components/Character";
import { Timer } from "../campaign-parts/timer";
import { ProgressSlideType } from "../progress";
import { DefaultSlideType } from "../default";
import { dynamicTranslate } from "./utils/translations";
import { PollSlideType } from "../poll";
import { Silhouette } from "./components/Silhouette";
import { useCustomModal } from "../../../../stores/customModal";
import { Dialog } from "./components/Dialog";
import { RawValue } from "./utils/RawValue";
import { ConversationVariable } from "./types/types";
import { DayTransition } from "./components/DayTransition";
import { bridge } from "../../../../utils/bridge";

export const VelvetGlitchSlide = ({
    slide,
    lang,
    progressTrackerSlides,
    pollSlides,
}: {
    slide: DefaultSlideType;
    lang: string;
    progressTrackerSlides?: ProgressSlideType[];
    pollSlides?: PollSlideType[];
}) => {
    const intl = useIntl();
    const { ctas, hideTitle, relatedMedia } = slide;

    const latestProgressTrackerSlide =
        progressTrackerSlides !== undefined
            ? progressTrackerSlides[progressTrackerSlides.length - 1]
            : undefined;

    const dateNumber = latestProgressTrackerSlide?.relatedMedia?.find(
        (media) => media.title === "date-number",
    )?.label;

    const latestPollSlide = pollSlides?.find((slide) =>
        slide.relatedMedia?.find(
            (media) =>
                media.title === "date-number" && media.label === dateNumber,
        ),
    );

    const latestConversationIsLocked = latestPollSlide === undefined;

    const phaseText = ` - ${dynamicTranslate({
        lang,
        key: "phase",
        phaseNumber: latestConversationIsLocked ? "1" : "2",
    })}`;

    const title = `${intl.formatMessage({ id: `globalText.langVelvetGlitchTitle${dateNumber}` })}${dateNumber !== "6" ? phaseText : ""}`;

    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const { trackButtonClick } = useSnowplowContext();
    useLocalStorageCleaner();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        () => {},
        title,
    );

    const { openCustomModal, closeCustomModal } = useCustomModal();

    const relatedMediaUrls = useMemo(
        () => [
            ...(relatedMedia
                ?.map((media) => media.image?.url)
                .filter((image) => image !== undefined) || []),

            ...(progressTrackerSlides
                ?.flatMap(
                    (slide) =>
                        slide.relatedMedia
                            ?.map((media) => media.image?.url)
                            .filter((image) => image !== undefined) || [],
                )
                .filter((image) => image !== undefined) || []),
            ...(pollSlides
                ?.flatMap(
                    (slide) =>
                        slide.relatedMedia
                            ?.map((media) => media.image?.url)
                            .filter((image) => image !== undefined) || [],
                )
                .filter((image) => image !== undefined) || []),
        ],
        [relatedMedia],
    );

    const relatedMediaLoaded = useImageLoader(relatedMediaUrls);
    const loaded = relatedMediaLoaded;

    const parsedCTAs = parseCTAs(ctas, handleClick);

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            environment: "--environment",
            "environment-layer": "--environment-layer",
            watch: "--watch",
            "heart-arrow-icon": "--heart-arrow-icon",
            meter: "--meter",
            "meter-glare": "--meter-glare",
            "meter-needle": "--meter-needle",
            "meter-center-pin": "--meter-center-pin",
            heart: "--heart",
            lock: "--lock",
            silhouette: "--silhouette",
            goal: "--goal",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        progressTrackerSlides?.forEach((slide) => {
            const dateNumber = slide.relatedMedia?.find((media) =>
                media.title?.toLowerCase().includes("date"),
            )?.label;

            const slideImageMap = {
                character: `--character-date${dateNumber}`,
                icon: `--icon-date${dateNumber}`,
            };

            Object.entries(slideImageMap).forEach(([title, cssVar]) => {
                const imageUrl = findRelatedMediaByTitle(
                    slide.relatedMedia ?? [],
                    title,
                );
                if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
            });
        });

        pollSlides?.forEach((slide) => {
            const dateNumber = slide.relatedMedia?.find((media) =>
                media.title?.toLowerCase().includes("date"),
            )?.label;

            const pollSlideImageMap = {
                "reward-1": `--reward-date${dateNumber}-1`,
                "reward-2": `--reward-date${dateNumber}-2`,
                "reward-3": `--reward-date${dateNumber}-3`,
                "character-clone": `--character-date${dateNumber}-clone`,
            };

            Object.entries(pollSlideImageMap).forEach(([title, cssVar]) => {
                const imageUrl = findRelatedMediaByTitle(
                    slide.relatedMedia ?? [],
                    title,
                );
                if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
            });
        });

        return styles;
    }, [relatedMedia, progressTrackerSlides]);

    const modalStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            watch: "--watch",
            "box-bg": "--box-bg",
            close: "--close",
            meter: "--meter",
            "meter-glare": "--meter-glare",
            "meter-needle": "--meter-needle",
            "meter-center-pin": "--meter-center-pin",
            heart: "--heart",
            "final-question-background": "--final-question-background",
            "final-question-zigzag": "--final-question-zigzag",
            "final-question-bubble": "--final-question-bubble",
            rays: "--rays",
            coins: "--coins",
            powerpoints: "--powerpoints",
            blings: "--blings",
            gems: "--gems",
            "chaos-drop": "--chaos-drop",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        progressTrackerSlides?.forEach((slide) => {
            const dateNumber = slide.relatedMedia?.find((media) =>
                media.title?.toLowerCase().includes("date"),
            )?.label;

            const slideImageMap = {
                character: `--character-date${dateNumber}`,
                environment: `--environment-date${dateNumber}`,
                icon: `--icon-date${dateNumber}`,
                "character-neutral": `--character-date${dateNumber}`,
            };

            Object.entries(slideImageMap).forEach(([title, cssVar]) => {
                const imageUrl = findRelatedMediaByTitle(
                    slide.relatedMedia ?? [],
                    title,
                );
                if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
            });
        });

        pollSlides?.forEach((slide) => {
            const dateNumber = slide.relatedMedia?.find((media) =>
                media.title?.toLowerCase().includes("date"),
            )?.label;

            const pollSlideImageMap = {
                "reward-1": `--reward-date${dateNumber}-1`,
                "reward-2": `--reward-date${dateNumber}-2`,
                "reward-3": `--reward-date${dateNumber}-3`,
                "reward-worst-date": `--reward-date${dateNumber}-worst`,
                "reward-perfect-date": `--reward-date${dateNumber}-perfect`,

                ...(dateNumber === "1"
                    ? {
                          "character-abouttocry": `--character-date${dateNumber}-abouttocry`,
                          "character-angry": `--character-date${dateNumber}-angry`,
                          "character-charred": `--character-date${dateNumber}-charred`,
                          "character-dancing": `--character-date${dateNumber}-dancing`,
                          "character-drool": `--character-date${dateNumber}-drool`,
                          "character-excited": `--character-date${dateNumber}-excited`,
                          "character-extremeblush": `--character-date${dateNumber}-extremeblush`,
                          "character-puppyeyes": `--character-date${dateNumber}-puppyeyes`,
                          "character-sad": `--character-date${dateNumber}-sad`,
                          "character-shy": `--character-date${dateNumber}-shy`,
                          "character-smile": `--character-date${dateNumber}-smile`,
                          "character-sweating": `--character-date${dateNumber}-sweating`,
                          "character-thinking": `--character-date${dateNumber}-thinking`,
                          "character-veryhappy": `--character-date${dateNumber}-veryhappy`,
                          "character-clone": `--character-date${dateNumber}-clone`,
                      }
                    : {}),
                ...(dateNumber === "2"
                    ? {
                          "character-leaning": `--character-date${dateNumber}-leaning`,
                          "character-flirty": `--character-date${dateNumber}-flirty`,
                          "character-laughing": `--character-date${dateNumber}-laughing`,
                          "character-confused": `--character-date${dateNumber}-confused`,
                          "character-cocky": `--character-date${dateNumber}-cocky`,
                          "character-disappointed": `--character-date${dateNumber}-disappointed`,
                          "character-excited": `--character-date${dateNumber}-excited`,
                          "character-bored": `--character-date${dateNumber}-bored`,
                          "character-heartbroken": `--character-date${dateNumber}-heartbroken`,
                          "character-shooting": `--character-date${dateNumber}-shooting`,
                          "character-clone": `--character-date${dateNumber}-clone`,
                      }
                    : {}),
                ...(dateNumber === "3"
                    ? {
                          "character-angry": `--character-date${dateNumber}-angry`,
                          "character-blush": `--character-date${dateNumber}-blush`,
                          "character-clone": `--character-date${dateNumber}-clone`,
                          "character-confident": `--character-date${dateNumber}-confident`,
                          "character-confused": `--character-date${dateNumber}-confused`,
                          "character-distracted": `--character-date${dateNumber}-distracted`,
                          "character-flirty": `--character-date${dateNumber}-flirty`,
                          "character-flirtysmile": `--character-date${dateNumber}-flirtysmile`,
                          "character-overexcited": `--character-date${dateNumber}-overexcited`,
                          "character-sad": `--character-date${dateNumber}-sad`,
                          "character-smile": `--character-date${dateNumber}-smile`,
                          "character-surprised": `--character-date${dateNumber}-surprised`,
                          "character-suspicious": `--character-date${dateNumber}-suspicious`,
                          "character-thinking": `--character-date${dateNumber}-thinking`,
                          "character-veryflirty": `--character-date${dateNumber}-veryflirty`,
                          "character-veryhappy": `--character-date${dateNumber}-veryhappy`,
                      }
                    : {}),
                ...(dateNumber === "4"
                    ? {
                          "character-bored": `--character-date${dateNumber}-bored`,
                          "character-clone": `--character-date${dateNumber}-clone`,
                          "character-confused": `--character-date${dateNumber}-confused`,
                          "character-disgusted": `--character-date${dateNumber}-disgusted`,
                          "character-diva": `--character-date${dateNumber}-diva`,
                          "character-drooling": `--character-date${dateNumber}-drooling`,
                          "character-excited": `--character-date${dateNumber}-excited`,
                          "character-flirty": `--character-date${dateNumber}-flirty`,
                          "character-inlove": `--character-date${dateNumber}-inlove`,
                          "character-irritated": `--character-date${dateNumber}-irritated`,
                          "character-sassy": `--character-date${dateNumber}-sassy`,
                          "character-shy": `--character-date${dateNumber}-shy`,
                          "character-smile": `--character-date${dateNumber}-smile`,
                          "character-suspicious": `--character-date${dateNumber}-suspicious`,
                      }
                    : {}),
                ...(dateNumber === "5"
                    ? {
                          "character-blushed": `--character-date${dateNumber}-blushed`,
                          "character-confused": `--character-date${dateNumber}-confused`,
                          "character-evilpose": `--character-date${dateNumber}-evilpose`,
                          "character-exhausted": `--character-date${dateNumber}-exhausted`,
                          "character-grandiosepose": `--character-date${dateNumber}-grandiosepose`,
                          "character-irritated": `--character-date${dateNumber}-irritated`,
                          "character-sassy": `--character-date${dateNumber}-sassy`,
                          "character-satisfied": `--character-date${dateNumber}-satisfied`,
                          "character-smile": `--character-date${dateNumber}-smile`,
                          "character-suspicious": `--character-date${dateNumber}-suspicious`,
                          "character-thinking": `--character-date${dateNumber}-thinking`,
                          "reward-sirius": `--reward-date${dateNumber}-sirius`,
                      }
                    : {}),
                ...(dateNumber === "6"
                    ? {
                          "character-clone": `--character-date${dateNumber}-clone`,
                          "character-shrugging": `--character-date${dateNumber}-shrugging`,
                          "character-fire": `--character-date${dateNumber}-fire`,
                      }
                    : {}),
            };

            Object.entries(pollSlideImageMap).forEach(([title, cssVar]) => {
                const imageUrl = findRelatedMediaByTitle(
                    slide.relatedMedia ?? [],
                    title,
                );
                if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
            });
        });

        return styles;
    }, [relatedMedia, progressTrackerSlides]);

    const trackerRef = React.useRef<HTMLDivElement>(null);

    const timerString = latestProgressTrackerSlide?.relatedMedia?.find(
        (media) => media.title === "timer",
    )?.label;

    const pollTimerString = latestPollSlide?.relatedMedia?.find(
        (media) => media.title === "timer",
    )?.label;

    const fillPercentage = latestProgressTrackerSlide?.tracker?.progress ?? 0;
    const barColor = latestProgressTrackerSlide?.tracker?.color;
    const trackerStyle = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
        "--bar-color": barColor,
    };
    const variablesRef = React.useRef<ConversationVariable[]>([]);

    const toggleVariables = (
        variables: {
            name: string;
            value: string;
        }[],
    ) => {
        if (variablesRef.current.length === 0) {
            variablesRef.current = variables;
            return;
        }

        let existingVariables = [...variablesRef.current];

        variables.forEach((variable) => {
            const updatedVariable = existingVariables.find(
                (v) => v.name === variable.name,
            );

            if (!updatedVariable) {
                existingVariables.push(variable);
                return;
            }

            updatedVariable.value = variable.value;

            existingVariables = existingVariables.map((v) =>
                v.name === variable.name ? updatedVariable : v,
            );
        });

        variablesRef.current = existingVariables;
    };

    const conversations = progressTrackerSlides
        ? progressTrackerSlides.map((trackerSlide) => {
              const character = trackerSlide.relatedMedia?.find(
                  (media) => media.title === "character",
              )?.label;

              const publishAt = trackerSlide.relatedMedia?.find(
                  (media) => media.title === "timer",
              )?.label;

              const dateNumber = trackerSlide.relatedMedia?.find(
                  (media) => media.title === "date-number",
              )?.label;

              return {
                  character,
                  publishAt,
                  name: `date${dateNumber}`,
              };
          })
        : [];

    const handleModalOpen = ({
        characterName,
        conversationName,
        handleLinkClick,
        trackButtonClick,
        pollSlideOptions,
        pollId,
    }: {
        characterName: string;
        conversationName: string;
        handleLinkClick: (cta: InboxCTA) => void;
        trackButtonClick: (
            id: string,
            label?: string,
            additionalInformation?: Record<string, unknown>,
        ) => void;
        pollSlideOptions?: PollSlideType["options"];
        pollId?: string;
    }) => {
        openCustomModal(
            <Dialog
                modalStyles={modalStyles}
                characterName={characterName}
                conversationName={conversationName}
                closeCustomModal={closeCustomModal}
                lang={lang}
                getLocalization={(id: string) =>
                    intl.formatMessage({ id: id.replace(". ", "") })
                }
                handleLinkClick={handleLinkClick}
                trackButtonClick={trackButtonClick}
                pollSlideOptions={pollSlideOptions}
                pollId={pollId}
                toggleVariables={toggleVariables}
                variablesRef={variablesRef}
            />,
        );

        trackButtonClick("velvetGlitchOpenCharacter", undefined, {
            characterName: characterName,
            characterId: conversationName,
        });

        // Override back button behavior to close the custom modal
        const backButtonEvent = new CustomEvent("backButtonConditionSet", {
            detail: {
                backButtonBehavior: "closeCustomModal",
            },
        });
        window.dispatchEvent(backButtonEvent);
    };

    return (
        <div
            className={`slide ${styles.slide} ${lang}`}
            style={{ ...relatedMediaStyles }}
        >
            <div className={`loader ${loaded ? "loaded" : ""}`}>
                <Loading light />
            </div>
            {loaded &&
                (latestConversationIsLocked || dateNumber === "6") &&
                dateNumber !== "1" && (
                    <>
                        {dateNumber === "6" &&
                        new Date(pollTimerString ?? "") <= new Date() ? (
                            <DayTransition
                                pollId={latestPollSlide?.id}
                                dateNumber={dateNumber}
                            />
                        ) : (
                            <DayTransition
                                pollId={
                                    // Get previous day pollId
                                    pollSlides?.find((slide) => {
                                        return slide.relatedMedia?.find(
                                            (media) =>
                                                media.title === "date-number" &&
                                                media.label ===
                                                    String(
                                                        Number(dateNumber) - 1,
                                                    ),
                                        );
                                    })?.id
                                }
                                dateNumber={String(Number(dateNumber) - 1)}
                            />
                        )}
                    </>
                )}
            {pollTimerString !== undefined ? (
                <Timer timerString={pollTimerString} lang={lang} />
            ) : (
                timerString !== undefined && (
                    <Timer timerString={timerString} lang={lang} />
                )
            )}
            {latestProgressTrackerSlide &&
                latestConversationIsLocked &&
                loaded && (
                    <div className={`tracker date${dateNumber}`}>
                        <div className="labels">
                            <div className="label">
                                <FormattedMessage
                                    id={`globalText.langVelvetGlitchTrackerLabel${dateNumber}`}
                                />
                            </div>
                            <div className="label">
                                {
                                    latestProgressTrackerSlide.milestones[0]
                                        ?.label
                                }
                            </div>
                        </div>
                        <div className="bar">
                            <div
                                ref={trackerRef}
                                className="progress"
                                style={trackerStyle}
                            >
                                <RawValue
                                    lang={lang}
                                    trackerProgress={
                                        latestProgressTrackerSlide.tracker
                                            .rawProgressInMillions
                                    }
                                    trackerProgressRef={trackerRef}
                                    key="current-day-raw-value"
                                />
                                <div className="icon"></div>
                            </div>
                        </div>
                        <div className="goal"></div>
                    </div>
                )}
            <div className={`content ${styles.content}`}>
                {title && !hideTitle && (
                    <>
                        <div className={`filter ${styles.filter}`} />

                        <div className="titles">
                            <div
                                className={`title ${styles.title}`}
                                data-title={title}
                            >
                                {title}
                            </div>

                            {latestConversationIsLocked ? (
                                <div className={`subtitle ${styles.subtitle}`}>
                                    <FormattedMessage
                                        id={`globalText.langVelvetGlitchInstruction${dateNumber}`}
                                    />
                                </div>
                            ) : (
                                <div className={`subtitle ${styles.subtitle}`}>
                                    <FormattedMessage id="globalText.langVelvetGlitchInstructionUnlocked" />
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className={`wrapper ${styles.wrapper}`}>
                    {parsedCTAs.length ? (
                        <div
                            className={`ctas ${
                                parsedCTAs.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {parsedCTAs.map(
                                (
                                    {
                                        label,
                                        onClick,
                                        variant,
                                    }: {
                                        label: string;
                                        onClick: () => void;
                                        variant: string;
                                    },
                                    index: number,
                                ) => (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                        size="sm"
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                ),
                            )}
                        </div>
                    ) : null}
                    <div className="claim-cta">
                        <Button
                            onClick={async () => {
                                const PPXP =
                                    "71f2b89d-d4da-4c80-9d85-e49a910c2f25";

                                const b = await bridge();
                                b.claimVoucherQuiet(PPXP);
                                b.close();
                            }}
                            variant="green"
                            size="sm"
                        >
                            <span className="buttonTextWrapper">
                                <FormattedMessage id="globalText.langClaim" />
                            </span>
                        </Button>
                    </div>
                </div>
            </div>
            <div className="characters-wrapper">
                <div className="layer"></div>
                <div
                    className={`characters ${dateNumber === "5" ? "clones" : ""}`}
                >
                    {[0, 1, 2, 3, 4, 5].map((index) => {
                        const set = conversations[index];

                        const datePollSlide = pollSlides?.find((slide) =>
                            slide.relatedMedia?.find(
                                (media) =>
                                    media.title === "date-number" &&
                                    media.label === String(index + 1),
                            ),
                        );

                        const isLatestDate = dateNumber === String(index + 1);

                        if (set) {
                            return (
                                <Fragment key={index}>
                                    {isLatestDate &&
                                        latestPollSlide &&
                                        dateNumber !== "6" && (
                                            <div className="actionButton">
                                                <Button
                                                    onClick={() => {
                                                        handleModalOpen({
                                                            characterName:
                                                                set.character ??
                                                                "",
                                                            conversationName:
                                                                set.name,
                                                            handleLinkClick:
                                                                handleClick,
                                                            trackButtonClick,
                                                            pollId: datePollSlide?.id,
                                                            pollSlideOptions:
                                                                datePollSlide?.options,
                                                        });
                                                    }}
                                                    variant="pink"
                                                >
                                                    <span className="buttonTextWrapper">
                                                        <FormattedMessage id="globalText.langVelvetGlitchModalCTA" />
                                                    </span>
                                                </Button>
                                            </div>
                                        )}
                                    <Character
                                        conversationName={set.name}
                                        handleModalOpen={handleModalOpen}
                                        characterName={set.character ?? ""}
                                        lang={lang}
                                        timer={null}
                                        handleLinkClick={handleClick}
                                        trackButtonClick={trackButtonClick}
                                        isLocked={
                                            pollSlides?.find((slide) =>
                                                slide.relatedMedia?.find(
                                                    (media) =>
                                                        media.title ===
                                                            "date-number" &&
                                                        media.label ===
                                                            String(index + 1),
                                                ),
                                            ) === undefined
                                        }
                                        pollId={datePollSlide?.id}
                                        pollSlideOptions={
                                            datePollSlide?.options
                                        }
                                        latest={
                                            String(index + 1) ===
                                            latestProgressTrackerSlide?.relatedMedia?.find(
                                                (media) =>
                                                    media.title ===
                                                    "date-number",
                                            )?.label
                                        }
                                    />
                                </Fragment>
                            );
                        } else {
                            return <Silhouette key={index} index={index} />;
                        }
                    })}
                </div>
            </div>
        </div>
    );
};
